import { apiService } from './api';
import { autoRoundPrice } from '../utils/priceRounding';

export interface GuestBlogPlacement {
  id: string;
  placementId: string;
  originalOrderId: string;
  originalOrderNumber: string;
  clientId: string;
  projectId: string;
  guestBlogSiteId: string;
  price: number;
  submissionType: 'GBS' | 'LI' | 'BOTH';
  contentText?: string;
  contentDocUrl?: string;
  contentUrl?: string;
  orderDate?: string; // ISO date string
  status: 'READY_TO_PUBLISH' | 'FOR_REVIEW' | 'NEED_CORRECTION' | 'COMPLETED' | 'FAILED' | 'APPROVED' | 'PLACED' | 'LIVE' | 'PENDING' | 'MAIL_SENT' | 'SITE_REPLACED' | 'HOLD';
  placedAt?: string;
  liveUrl?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  createdById: string;
  client: {
    id: string;
    name: string;
    email: string;
    percentage?: number;
  };
  project: {
    id: string;
    projectName: string;
    websiteUrl?: string;
    companyName?: string;
  };
  guestBlogSite: {
    id: string;
    site_url: string;
    da: number;
    dr: number;
    base_price: number;
    li_base_price?: number;
    publisher: {
      id: string;
      publisherName: string;
      email?: string;
    };
  };
  publishers?: {
    id: string;
    publisherName: string;
    email?: string;
  };
  createdBy: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

export interface CreatePlacementsRequest {
  orderIds: string[];
}

export interface UpdatePlacementRequest {
  status?: 'READY_TO_PUBLISH' | 'FOR_REVIEW' | 'NEED_CORRECTION' | 'COMPLETED' | 'FAILED' | 'APPROVED' | 'PLACED' | 'LIVE' | 'PENDING' | 'MAIL_SENT' | 'SITE_REPLACED' | 'HOLD';
  liveUrl?: string;
  notes?: string;
  contentText?: string;
  contentDocUrl?: string;
  contentUrl?: string;
}

export interface FileUploadResponse {
  success: boolean;
  data: {
    url: string;
    filename: string;
    originalName: string;
    size: number;
    mimetype?: string;
  };
}

export interface GetPlacementsParams {
  page?: number;
  limit?: number;
  clientId?: string;
  projectId?: string;
  publisherId?: string;
  search?: string;
  status?: string;
  month?: number;
  year?: number;
  sortBy?: 'createdAt' | 'price' | 'orderDate' | 'status';
  sortOrder?: 'asc' | 'desc';
}

export interface PlacementsResponse {
  success: boolean;
  data: GuestBlogPlacement[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

class GuestBlogPlacementService {
  async getPlacements(params: GetPlacementsParams = {}): Promise<PlacementsResponse> {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.clientId) queryParams.append('clientId', params.clientId);
    if (params.projectId) queryParams.append('projectId', params.projectId);
    if (params.publisherId) queryParams.append('publisherId', params.publisherId);
    if (params.search) queryParams.append('search', params.search);
    if (params.status) queryParams.append('status', params.status);
    if (params.month) queryParams.append('month', params.month.toString());
    if (params.year) queryParams.append('year', params.year.toString());
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const url = queryParams.toString() ? `/guest-blog-placements?${queryParams.toString()}` : '/guest-blog-placements';
    return await apiService.get<PlacementsResponse>(url);
  }

  async createPlacements(data: CreatePlacementsRequest): Promise<{ success: boolean; data: GuestBlogPlacement[]; message: string }> {
    return await apiService.post<{ success: boolean; data: GuestBlogPlacement[]; message: string }>('/guest-blog-placements', data);
  }

  async updatePlacement(id: string, data: UpdatePlacementRequest): Promise<{ success: boolean; data: GuestBlogPlacement; message: string }> {
    return await apiService.put<{ success: boolean; data: GuestBlogPlacement; message: string }>(`/guest-blog-placements/${id}`, data);
  }

  async deletePlacement(id: string): Promise<{ success: boolean; message: string }> {
    return await apiService.delete<{ success: boolean; message: string }>(`/guest-blog-placements/${id}`);
  }

  async uploadContentDocument(file: File): Promise<FileUploadResponse> {
    const formData = new FormData();
    formData.append('contentDoc', file);
    return await apiService.upload<FileUploadResponse>('/guest-blog-placements/upload-content-doc', formData);
  }

  // Utility functions
  formatPrice(price: number): string {
    const roundedPrice = autoRoundPrice(price);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(roundedPrice);
  }

  // Legacy status mapping for backward compatibility
  mapLegacyStatus(status: string): string {
    switch (status) {
      case 'PENDING': return 'READY_TO_PUBLISH';
      case 'APPROVED': return 'READY_TO_PUBLISH';
      case 'PLACED': return 'FOR_REVIEW';
      case 'LIVE': return 'COMPLETED';
      default: return status;
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'PENDING': return 'Pending';
      case 'READY_TO_PUBLISH': return 'Ready to Publish';
      case 'APPROVED': return 'Approved';
      case 'PLACED': return 'Placed';
      case 'FOR_REVIEW': return 'For Review';
      case 'NEED_CORRECTION': return 'Need Correction';
      case 'LIVE': return 'Live';
      case 'COMPLETED': return 'Completed';
      case 'FAILED': return 'Failed';
      case 'MAIL_SENT': return 'Mail Sent';
      case 'SITE_REPLACED': return 'Site Replaced';
      default: return status;
    }
  }

  getStatusColor(status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' {
    switch (status) {
      case 'PENDING': return 'default';
      case 'READY_TO_PUBLISH': return 'info';
      case 'APPROVED': return 'secondary';
      case 'PLACED': return 'primary';
      case 'FOR_REVIEW': return 'warning';
      case 'NEED_CORRECTION': return 'error';
      case 'LIVE': return 'success';
      case 'COMPLETED': return 'success';
      case 'FAILED': return 'error';
      case 'MAIL_SENT': return 'info';
      case 'SITE_REPLACED': return 'warning';
      default: return 'default';
    }
  }

  // Get status color for custom styling (hex colors)
  getStatusHexColor(status: string): string {
    switch (status) {
      case 'PENDING': return '#6B7280'; // Gray
      case 'READY_TO_PUBLISH': return '#3B82F6'; // Blue
      case 'APPROVED': return '#8B5CF6'; // Purple
      case 'PLACED': return '#06B6D4'; // Cyan
      case 'FOR_REVIEW': return '#F59E0B'; // Orange
      case 'NEED_CORRECTION': return '#EF4444'; // Red
      case 'LIVE': return '#10B981'; // Green
      case 'COMPLETED': return '#059669'; // Dark Green
      case 'FAILED': return '#EF4444'; // Red
      case 'MAIL_SENT': return '#3B82F6'; // Blue
      case 'SITE_REPLACED': return '#F59E0B'; // Orange
      default: return '#6B7280'; // Gray
    }
  }

  // Get all available statuses for filtering
  getAvailableStatuses(): Array<{value: string, label: string, color: string}> {
    return [
      { value: 'READY_TO_PUBLISH', label: 'Ready to Publish', color: '#3B82F6' },
      { value: 'FOR_REVIEW', label: 'For Review', color: '#F59E0B' },
      { value: 'NEED_CORRECTION', label: 'Need Correction', color: '#EF4444' },
      { value: 'COMPLETED', label: 'Completed', color: '#10B981' },
      { value: 'MAIL_SENT', label: 'Mail Sent', color: '#3B82F6' },
      { value: 'SITE_REPLACED', label: 'Site Replaced', color: '#F59E0B' },
      { value: 'HOLD', label: 'Hold', color: '#8B5CF6' }
    ];
  }

  // Utility function to get full file URL
  getFileUrl(relativePath: string): string {
    if (!relativePath) return '';
    // If it's already a full URL, return as-is
    if (relativePath.startsWith('http')) return relativePath;
    // Otherwise, prepend the backend base URL
    const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    return `${backendUrl}${relativePath}`;
  }

  // Utility function to get filename from URL
  getFileName(fileUrl: string): string {
    if (!fileUrl) return 'document';
    const parts = fileUrl.split('/');
    return parts[parts.length - 1] || 'document';
  }

  // Function to download file
  downloadFile(fileUrl: string, downloadName: string): void {
    if (!fileUrl) return;
    
    // Use the correct base URL (already includes /api)
    const backendUrl = process.env.NODE_ENV === 'production' 
      ? 'https://links.usehypwave.com/api'
      : 'http://localhost:3001/api';
    
    try {
      // Use the placement-specific download endpoint
      const filename = this.getFileName(fileUrl);
      const downloadUrl = `${backendUrl}/guest-blog-placements/download/${filename}?token=download-access`;
      
      // Create a temporary link element and trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = downloadName;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
      // Fallback: try direct file URL
      const fullUrl = this.getFileUrl(fileUrl);
      window.open(fullUrl, '_blank');
    }
  }
}

export const guestBlogPlacementService = new GuestBlogPlacementService();